import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useTable, usePagination } from 'react-table';
import TableBox from '../../common/TableBox';
import ReactTablePagination from '../../../common/ReactTablePagination';
import EmptyState from '../../../common/EmptyState';
import MessageStatus from './MessageStatus';
import MessageBatchMoreInfo from './MessageBatchMoreInfo';
import { Link } from 'react-router-dom';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Box,
  Text,
  Heading,
  Flex,
  Stack,
  Button,
  Badge,
  useMediaQuery,
  Tooltip,
} from '@chakra-ui/react';
import { CustomCsvDownloadIcon } from '../../../theme/icons/CustomCsvDownloadIcon';
import { ChevronDownIcon } from '@chakra-ui/icons';
import MessageBatchProgressDrawer from './MessageBatchProgressDrawer';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';
import LockedActionButton from '../../common/LockedActionButton';
import SmsStatusDelayNotice from './SmsStatusDelayNotice';

const MessagingHistoryTable = ({
  columns,
  heading,
  data,
  fetchData,
  isLoading,
  pageCount,
  total,
  cancel,
  tableType,
  filterEntity,
}) => {
  const isReadOnly = useIsReadOnly();
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setHiddenColumns,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount,
      initialState: { pageIndex: 0 },
    },
    usePagination
  );

  const [isLargerThan2XL] = useMediaQuery('(min-width: 96em)');
  const [isLargerThanXL] = useMediaQuery('(min-width: 80em)');
  const [isLargerThanMD] = useMediaQuery('(min-width: 48em)');
  const [isLargerThanSM] = useMediaQuery('(min-width: 30em)');
  const [isLargerThanS] = useMediaQuery('(min-width: 18em)');
  const [isLargerThanXS] = useMediaQuery('(min-width: 10em)');

  const [collapsedRows, setCollapsedRows] = useState(new Set());
  const toggleRowCollapse = (id) => {
    setCollapsedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  useEffect(() => {
    fetchData({ pageIndex, pageSize });
  }, [fetchData, pageIndex, pageSize]);

  useEffect(() => {
    if (isLargerThan2XL) {
      setHiddenColumns([]);
    } else if (isLargerThanXL) {
      if (tableType === 'scheduled') {
        setHiddenColumns([]);
      } else {
        setHiddenColumns(['content']);
      }
    } else if (isLargerThanMD) {
      setHiddenColumns(['content', 'completedAt']);
    } else if (isLargerThanSM) {
      setHiddenColumns([
        'content',
        'completedAt',
        'recipients',
        'status',
        'cancelAction',
      ]);
    } else if (isLargerThanS) {
      setHiddenColumns([
        'content',
        'completedAt',
        'recipients',
        'errors',
        'messageType',
        'status',
        'cancelAction',
      ]);
    } else {
      setHiddenColumns([
        'content',
        'completedAt',
        'recipients',
        'errors',
        'messageType',
        'status',
        'cancelAction',
      ]);
    }
  }, [
    isLargerThan2XL,
    isLargerThanXL,
    isLargerThanMD,
    isLargerThanSM,
    isLargerThanS,
    isLargerThanXS,
    setHiddenColumns,
    tableType,
  ]);

  if (isLoading) {
    return (
      <Box textAlign="center">
        <Spinner />
      </Box>
    );
  }

  if (data.length === 0 && !isLoading) {
    return (
      <EmptyState
        heading={
          tableType === 'scheduled'
            ? 'No Scheduled Messages'
            : 'No Messaging History'
        }
        text={
          tableType === 'scheduled'
            ? 'Click on the button below to start scheduling your messages.'
            : 'Click on the button below to start sending your messages.'
        }
      >
        {isReadOnly ? (
          <LockedActionButton reason="send-messages" size="sm">
            New Message
          </LockedActionButton>
        ) : (
          <Button alt="New Message" size="sm" as={Link} to="/messaging/create">
            New Message
          </Button>
        )}
      </EmptyState>
    );
  }

  return (
    <>
      <TableBox>
        <Heading size="lg" mb="16px">
          {heading}{' '}
          <Text
            as="span"
            textStyle="headingFamilyMedium"
            fontSize="18px"
            fontWeight="500"
          >
            (
            <Box as="span" color="brand">
              {total}
            </Box>
            )
          </Text>
        </Heading>
        {filterEntity?.mobile_messaging_enabled &&
          tableType !== 'scheduled' && <SmsStatusDelayNotice />}
        <Table {...getTableProps()}>
          <Thead>
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <Th {...column.getHeaderProps()} style={column.style}>
                    {column.render('Header')}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              const processed = row.original?.progress?.processed;
              const total = row.original?.progress?.total;
              const hasProgressValue =
                typeof processed === 'number' &&
                typeof total === 'number' &&
                total > 0;
              const isInProgress = [
                'processing',
                'sending',
                'validating',
                'cancelling',
              ].includes(row.original.status);
              const showProgressRow = isInProgress && hasProgressValue;
              const isCollapsed = collapsedRows.has(row.original.id);
              const renderCollapseToggle = () =>
                showProgressRow ? (
                  <Box
                    as="button"
                    ml={2}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRowCollapse(row.original.id);
                    }}
                    aria-label="Toggle row details"
                  >
                    <ChevronDownIcon
                      color="gray.500"
                      transition="transform 0.2s ease"
                      style={{
                        transform: isCollapsed
                          ? 'rotate(-180deg)'
                          : 'rotate(0deg)',
                      }}
                      fontSize={22}
                    />
                  </Box>
                ) : null;

              return (
                <React.Fragment key={row.id}>
                  <Tr
                    {...row.getRowProps()}
                    _hover={{
                      cursor: 'default',
                    }}
                  >
                    {row.cells.map((cell) => {
                      const cancelDisabled =
                        isReadOnly ||
                        !!(
                          cell.row.original.scheduledAtRaw &&
                          moment(cell.row.original.scheduledAtRaw).isBefore(
                            moment().add(30, 'minutes')
                          )
                        );
                      return (
                        <Td {...cell.getCellProps()} style={cell.column.style}>
                          {cell.column.id === 'status' ? (
                            <MessageStatus status={cell.value} type="batch" />
                          ) : cell.column.id === 'cancelAction' ? (
                            <Tooltip
                              label="You can cancel messages up to 30 minutes before the scheduled time."
                              hasArrow
                              placement="top"
                              fontSize="12px"
                              bg="inkBlue"
                              color="white"
                              borderRadius="6px"
                              textAlign="center"
                              p="10px"
                            >
                              <Box
                                as="span"
                                display="inline-block"
                                pointerEvents="auto"
                              >
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  px="19px"
                                  isDisabled={cancelDisabled}
                                  onClick={() => {
                                    cancel(
                                      cell.row.original.id,
                                      cell.row.original.messageType
                                    );
                                    fetchData({ pageSize: 10, pageIndex: 0 });
                                  }}
                                >
                                  <Text>Cancel</Text>
                                </Button>
                              </Box>
                            </Tooltip>
                          ) : cell.column.id === 'errors' ? (
                            cell.row.original.messageType === 'Wallet' &&
                            cell.row.original.errorsCsv ? (
                              <Link to={cell.row.original.errorsCsv}>
                                <CustomCsvDownloadIcon
                                  color="brand"
                                  boxSize={7}
                                />
                              </Link>
                            ) : cell.value > 0 ? (
                              <Badge
                                variant="outline"
                                colorScheme="red"
                                fontWeight="bold"
                                cursor="pointer"
                                onClick={() => {
                                  cell.column.setTabIndex?.(1);
                                }}
                              >
                                {cell.value}
                              </Badge>
                            ) : null
                          ) : cell.column.id === 'moreInfo' ? (
                            <MessageBatchMoreInfo
                              cell={cell}
                              cancel={cancel}
                              fetchData={fetchData}
                              pageIndex={pageIndex}
                              pageSize={pageSize}
                              cancelDisabled={cancelDisabled}
                            />
                          ) : cell.column.id === 'name' ? (
                            isLargerThanXS && !isLargerThanMD ? (
                              <Flex alignItems="center">
                                <MessageStatus
                                  status={cell.row.original.status}
                                  type="batch"
                                  showLabel={false}
                                />
                                <Text ml={3}>{cell.value}</Text>
                                {renderCollapseToggle()}
                              </Flex>
                            ) : (
                              <Flex align="center">
                                <Text as="span">{cell.render('Cell')}</Text>
                                {renderCollapseToggle()}
                              </Flex>
                            )
                          ) : (
                            cell.render('Cell')
                          )}
                        </Td>
                      );
                    })}
                  </Tr>
                  {showProgressRow && (
                    <Tr
                      key={`${row.id}-drawer`}
                      display={isCollapsed ? 'none' : 'table-row'}
                    >
                      <Td
                        colSpan={row.cells.length}
                        p={0}
                        border="none"
                        display={isCollapsed ? 'none' : 'table-cell'}
                      >
                        <Box
                          px={6}
                          py={4}
                          bg="lightGrey2"
                          transition="all 0.4s ease"
                        >
                          <MessageBatchProgressDrawer cell={row} />
                        </Box>
                      </Td>
                    </Tr>
                  )}
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
        {data.length > 0 && (
          <Flex
            mt={6}
            flexDirection={{ base: 'column-reverse', '2xl': 'row' }}
            justifyContent={{ base: 'none', '2xl': 'space-between' }}
          >
            <Stack
              spacing="18px"
              direction={{ base: 'column', sm: 'row' }}
              shouldWrapChildren="true"
            ></Stack>

            <ReactTablePagination
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageOptions={pageOptions}
              pageCount={pageCount}
              gotoPage={gotoPage}
              nextPage={nextPage}
              previousPage={previousPage}
              setPageSize={setPageSize}
              pageIndex={pageIndex}
              pageSize={pageSize}
            />
          </Flex>
        )}
      </TableBox>
    </>
  );
};

export default MessagingHistoryTable;
